import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Collapse,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Settings,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import "./SideBar.css";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";

import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
// import DownloadIcon from '@mui/icons-material/Download';
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";

const Sidebar = ({ open, onClose }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("1024"));
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [hovering, setHovering] = useState(false);
  const [expanded, setExpanded] = useState(true); // Track if sidebar is expanded or minimized

  const toggleSubMenu = (text) => {
    setOpenSubMenus((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const toggleSidebar = () => {
    setExpanded((prev) => !prev);
  };

  const menuItems = [
    {
      text: "My accounts",
      icon: <DashboardRoundedIcon size={20} />,
      link: "/my-account",
    },
    {
      text: "Deposit",
      icon: <ArrowUpFromLine size={20} />,
      link: "/deposit",
    },
    {
      text: "Withdrawal",
      icon: <ArrowDownToLine size={20} />,
      link: "/withdrawal",
    },
    {
      text: "Transaction History",
      icon: <HistoryRoundedIcon size={20} />,
      link: "/transaction-history",
    },
    {
      text: "Wallet",
      icon: <AccountBalanceWalletIcon size={20} />,
      link: "/crypto-wallet",
    },

    {
      text: "Analytics",
      icon: <PieChartRoundedIcon />,
      link: "/trading",
      children: [
        // {
        //   text: "Economic Calendar",
        //   link: "#",
        // },
        {
          text: "Analyst Views",
          link: "/analytics-views",
        },
        // {
        //   text: "Market News",
        //   link: "#",
        // },
      ],
    },
    {
      text: "Social Trading",
      icon: <PeopleAltIcon size={20} />,
      link: "/social-trading",
    },
    {
      text: "Performance",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.2495 16.5052H3.74953V15.0052H11.2495V16.5052ZM16.4995 3L12.9483 7.46625L12.7833 7.66875L8.56078 13.0613C8.42142 13.2006 8.25597 13.3112 8.07388 13.3866C7.89179 13.462 7.69663 13.5008 7.49953 13.5008C7.30244 13.5008 7.10728 13.462 6.92519 13.3866C6.7431 13.3112 6.57765 13.2006 6.43828 13.0613C6.29892 12.9219 6.18837 12.7564 6.11294 12.5743C6.03752 12.3923 5.9987 12.1971 5.9987 12C5.9987 11.8029 6.03752 11.6077 6.11294 11.4257C6.18837 11.2436 6.29892 11.0781 6.43828 10.9388L16.4995 3ZM1.98328 5.4255C0.899725 7.16057 0.510067 9.24035 0.892035 11.25H1.49953V10.5C1.49208 9.02334 1.93416 7.57938 2.76703 6.36L1.98328 5.4255ZM8.99953 1.5C7.85251 1.49993 6.71816 1.73993 5.66945 2.20454C4.62073 2.66916 3.68087 3.34812 2.91028 4.19775L3.76828 5.22C4.78651 4.28392 6.11645 3.75996 7.49953 3.75C8.89013 3.75955 10.2269 4.28869 11.2473 5.2335L13.9098 3.132C12.4919 2.07263 10.7695 1.50016 8.99953 1.5ZM14.122 8.4L13.9473 8.61525L13.4013 9.31275C13.4655 9.70527 13.4983 10.1023 13.4995 10.5V11.25H17.107C17.4629 9.37304 17.1489 7.43074 16.2198 5.7615L14.122 8.4Z"
            fill="white"
          />
        </svg>
      ),
      link: "/performance",
      children: [
        {
          text: "Summary",
          link: "/performance/summary",
        },
        {
          text: "History of orders",
          link: "/performance/history-of-orders",
        },
        {
          text: "Regulus Benefits",
          link: "/performance/benefits",
        },
      ],
    },
    {
      text: "Support Hub",
      icon: <ManageAccountsIcon size={20} />,
      link: "/support-home",
    },
    {
      text: "Settings",
      icon: <SettingsIcon size={20} />,
      link: "/settings",
      children: [
        {
          text: "Profile",
          link: "/settings/profile",
        },
        {
          text: "Security Settings",
          link: "/settings/security-settings",
        },
        {
          text: "Trading settings",
          link: "/settings/trading-terminals",
        },
        {
          text: "Trading Conditions",
          link: "/settings/trading-condition",
        },
        {
          text: "Virtual Private Server",
          link: "/settings/virtual-private-server",
        },
      ],
    },
  ];

  // const bottomMenuItems = [
  //   { text: "Support Hub", icon: <HelpCircle size={20} />, link: "/help" },
  //   { text: "Settings", icon: <Settings size={20} />, link: "/settings" },
  // ];

  const handleMouseEnter = () => {
    if (!isMobile) {
      setHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setHovering(false);
    }
  };
  const location = useLocation(); // Get current route

  const renderMenuItem = (item, depth = 0) => {
    const isMinimized = !isMobile && !expanded && !hovering;
    const isActive = location.pathname.startsWith(item.link); // Keep parent menu active on child pages

    const menuItem = (
      <NavLink to={item.link}>
        <ListItem
          disablePadding
          component="a"
          href={item.link}
          onClick={(e) => {
            if (item.children) {
              e.preventDefault();
              toggleSubMenu(item.text);
            }
          }}
          selected={isActive}
        >
          <ListItemButton
            // sx={{
            //   py: 1.5,
            //   pl: isMinimized ? 2 : depth * 2 + 2,
            //   minHeight: 48,
            //   justifyContent: isMinimized ? "center" : "flex-start",
            //   "&:hover": {
            //     backgroundColor: "rgba(0, 165, 115, 0.08)",
            //   },
            //   "&.Mui-selected": {
            //     backgroundColor: isActive
            //       ? "rgba(0, 165, 115, 0.12)"
            //       : "transparent",
            //     "&:hover": {
            //       backgroundColor: "rgba(0, 165, 115, 0.16)",
            //     },
            //   },
            // }}
            sx={{
              py: 1.5,
              pl: isMinimized ? 2 : depth * 2 + 2,
              minHeight: 48,
              justifyContent: isMinimized ? "center" : "flex-start",
              background: isActive
                ? "linear-gradient(90deg, #ae8625 0%, #f7ef8a 39.02%, #d2ac47 76.5%, #edc967 97.99%);"
                : "transparent",
              borderRadius: "10px",
              color: isActive ? "#000" : "#fff",
              fontWeight: "800",
              "&:hover": { background: "rgba(0, 165, 115, 0.08)" },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: isMinimized ? 0 : 40,
                mr: isMinimized ? 0 : 2,
              }}
            >
              {item.icon}
            </ListItemIcon>
            {(!isMinimized || hovering) && (
              <>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: isMinimized ? 0 : 1,
                    "& .MuiListItemText-primary": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                />
                {item.children &&
                  (openSubMenus[item.text] ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  ))}
              </>
            )}
          </ListItemButton>
        </ListItem>
      </NavLink>
    );

    return (
      <React.Fragment key={item.text}>
        {isMinimized ? (
          <Tooltip title={item.text} placement="right">
            {menuItem}
          </Tooltip>
        ) : (
          menuItem
        )}
        {item.children && (!isMinimized || hovering) && (
          <Collapse in={openSubMenus[item.text]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        position: "absolute",
        top: "10px",
        left: 0,
        height: "100%",
        width: !isMobile && !expanded && !hovering ? 65 : 250,
        zIndex: 10, // Ensure it's on top of the content area
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <List sx={{ flexGrow: 1 }} className="mobile-sidebar">
        {menuItems.map((item) => renderMenuItem(item))}
      </List>
      <Divider />
      {/* <List>
        {bottomMenuItems.map((item) => renderMenuItem(item))}
      </List> */}
      {!isMobile && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <Tooltip title={expanded ? "Collapse sidebar" : "Expand sidebar"}>
            <IconButton
              onClick={toggleSidebar}
              size="small"
              sx={{
                backgroundColor: "#1D1D1D",
                "&:hover": {
                  background:
                    "var(--Gold-Gradient, linear-gradient(90deg, #ae8625 0%, #f7ef8a 39.02%, #d2ac47 76.5%, #edc967 97.99%))",
                },
              }}
            >
              {expanded ? (
                <PanelLeftClose size={20} />
              ) : (
                <PanelLeftOpen size={20} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  return (
    <div className="sidebarOne">
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        open={isMobile ? open : true}
        onClose={onClose}
        sx={{
          width: !isMobile && !expanded && !hovering ? 65 : 250,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: !isMobile && !expanded && !hovering ? 65 : 250,
            boxSizing: "border-box",
            backgroundColor: "#000",
            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            top: "65px",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </div>
  );
};

export default Sidebar;
